export function initAccountMenu() {
    const menuElements = Array.from(document.querySelectorAll('[data-account-menu]'));
    if (menuElements.length === 0) {
        return;
    }

    const CLOSE_ANIMATION_DURATION_MS = 120;
    const closeAnimationTimers = new WeakMap();

    /** @type {HTMLElement | null} */
    let activeMenu = null;

    const getMenuParts = (menu) => {
        const trigger = menu.querySelector('[data-account-menu-trigger]');
        const dropdown = menu.querySelector('[data-account-menu-dropdown]');

        if (!(trigger instanceof HTMLButtonElement) || !(dropdown instanceof HTMLElement)) {
            return null;
        }

        return { trigger, dropdown };
    };

    const clearCloseAnimationTimer = (menu) => {
        const closeTimer = closeAnimationTimers.get(menu);
        if (typeof closeTimer === 'number') {
            window.clearTimeout(closeTimer);
            closeAnimationTimers.delete(menu);
        }
    };

    const setMenuState = (menu, isOpen) => {
        const parts = getMenuParts(menu);
        if (parts === null) {
            return;
        }

        const { trigger, dropdown } = parts;
        clearCloseAnimationTimer(menu);
        dropdown.classList.remove('account-menu-dropdown--closing');
        menu.classList.toggle('is-open', isOpen);
        trigger.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        dropdown.hidden = isOpen === false;
    };

    const closeMenu = (menu, options = {}) => {
        const parts = getMenuParts(menu);
        if (parts === null) {
            return;
        }

        const { trigger, dropdown } = parts;
        const shouldCloseImmediately = options.immediate === true
            || dropdown.hidden
            || prefersReducedMotion();

        clearCloseAnimationTimer(menu);

        if (shouldCloseImmediately) {
            setMenuState(menu, false);
        } else {
            menu.classList.remove('is-open');
            trigger.setAttribute('aria-expanded', 'false');
            dropdown.classList.add('account-menu-dropdown--closing');

            const closeTimer = window.setTimeout(() => {
                setMenuState(menu, false);
                closeAnimationTimers.delete(menu);
            }, CLOSE_ANIMATION_DURATION_MS);
            closeAnimationTimers.set(menu, closeTimer);
        }

        if (activeMenu === menu) {
            activeMenu = null;
        }
    };

    const openMenu = (menu) => {
        if (activeMenu instanceof HTMLElement && activeMenu !== menu) {
            closeMenu(activeMenu, { immediate: true });
        }

        setMenuState(menu, true);
        activeMenu = menu;
    };

    menuElements.forEach((menu) => {
        if (!(menu instanceof HTMLElement)) {
            return;
        }

        const trigger = menu.querySelector('[data-account-menu-trigger]');
        const dropdown = menu.querySelector('[data-account-menu-dropdown]');
        if (!(trigger instanceof HTMLButtonElement) || !(dropdown instanceof HTMLElement)) {
            return;
        }

        setMenuState(menu, false);

        trigger.addEventListener('click', () => {
            const shouldOpen = dropdown.hidden || dropdown.classList.contains('account-menu-dropdown--closing');

            if (shouldOpen) {
                openMenu(menu);
                return;
            }

            closeMenu(menu);
        });

        menu.addEventListener('keydown', (event) => {
            if (event.key !== 'Escape') {
                return;
            }

            closeMenu(menu);
            trigger.focus();
        });

        dropdown.addEventListener('click', (event) => {
            const target = event.target;
            if (!(target instanceof HTMLElement)) {
                return;
            }

            if (target.closest('a, button') === null) {
                return;
            }

            closeMenu(menu);
        });
    });

    document.addEventListener('click', (event) => {
        if (!(activeMenu instanceof HTMLElement) || !(event.target instanceof Node)) {
            return;
        }

        if (activeMenu.contains(event.target)) {
            return;
        }

        closeMenu(activeMenu);
    });

    window.addEventListener('resize', () => {
        if (!(activeMenu instanceof HTMLElement)) {
            return;
        }

        closeMenu(activeMenu, { immediate: true });
    });
}

function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
